// Shared source of truth for the plan-comparison data. Imported by both the
// "Decide in 10 seconds" section on the Product Brochure and the printable
// Business-Case one-pager at /business-case. Edit copy in one place, it
// updates both surfaces.
//
// Each row carries:
//   label                — short feature name
//   free/premium/turbo   — availability boolean per tier
//   service_impact       — impact on the doctor's clinical/operational quality
//   acquisition_impact   — impact on new-patient acquisition or retention

export const COMPARE_ROWS = [
  {
    label: 'Your own personal doctor website (docsite.care/dr/…)',
    free: true, premium: true, turbo: true,
    service_impact: 'A single source of truth for your practice — no conflicting listings across 3rd-party aggregators.',
    acquisition_impact: 'You own your Google ranking. Patients searching your name land on YOUR site — not on a competitor\'s paid ad.',
  },
  {
    label: 'Multi-location, multi-service scheduling (OPD/Surgery/…)',
    free: true, premium: true, turbo: true,
    service_impact: 'Patients book the right service for their case — zero front-desk confusion between OPD, consulting, and OT days.',
    acquisition_impact: 'Higher conversion: patients see their exact slot by service and book in under 60 seconds.',
  },
  {
    label: 'Structured cancellation + 1-hr buffer + patient alerts',
    free: true, premium: true, turbo: true,
    service_impact: 'Every cancellation is reasoned, timely, and audit-logged. Protects your reputation during unavoidable disruptions.',
    acquisition_impact: '"She was upfront about why she cancelled" — trust compounds, and retained patients bring referrals.',
  },
  {
    label: 'Install-as-an-App (PWA) with offline browsing',
    free: true, premium: true, turbo: true,
    service_impact: 'Patients can access your clinic details, timings, and directions even in low-network zones around your hospital.',
    acquisition_impact: 'App-like stickiness without an app store fight. Your home-screen icon drives repeat visits like a real app.',
  },
  {
    label: 'Your patients get "Scan & Save" + zero-touch onboarding',
    free: true, premium: true, turbo: true,
    service_impact: 'First-visit patients finish digital onboarding before leaving your clinic — their record is active from day one.',
    acquisition_impact: 'Every walk-in becomes a long-term digital patient. No drop-off between first consult and second booking.',
  },
  {
    label: 'Clinic display name + address + embedded map',
    free: false, premium: true, turbo: true,
    service_impact: 'Patients arrive at the right clinic, on time — your reception stops answering "where are you located?"',
    acquisition_impact: 'First-time conversion jumps noticeably: embedded map reduces the "I\'ll search later" drop-off.',
  },
  {
    label: 'Per-procedure price ranges shown publicly',
    free: false, premium: true, turbo: true,
    service_impact: 'Patients arrive price-calibrated. No awkward money conversations at the counter; clinical time stays clinical.',
    acquisition_impact: 'Price transparency filters tyre-kickers out. The patients who DO book are high-intent and convert at much higher rates.',
  },
  {
    label: 'Patient ↔ Doctor booking chat (3-message cap)',
    free: false, premium: true, turbo: true,
    service_impact: 'Pre-appointment coordination (fasting, reports, forms) without messy WhatsApp threads spilling into off-hours.',
    acquisition_impact: 'Fewer no-shows. Patients feel heard before arrival — clinical warmth drives 5-star outcomes.',
  },
  {
    label: 'Did-Not-Attend (DNA) tracking + reliability score',
    free: false, premium: true, turbo: true,
    service_impact: 'Protects your clinic time from serial no-shows — you allocate slots to patients who show up.',
    acquisition_impact: 'Reliable patients get priority. You build a roster of engaged, respectful patients — not slot-hoarders.',
  },
  {
    label: '2 AI pulls / month (articles, SEO pages, AI check, blend)',
    free: false, premium: false, turbo: true,
    service_impact: 'Your clinical voice stays yours — AI only polishes grammar and structure. Never invents facts, never rewrites medicine.',
    acquisition_impact: 'Each published article is an evergreen patient magnet. Two pieces a month = a content moat competitors can\'t replicate.',
  },
  {
    label: 'Clinic Kit — printable QR poster (powers patient auto-save)',
    free: false, premium: false, turbo: true,
    service_impact: 'Patients exit your clinic with your full digital profile saved — in under 3 seconds, no typing, no app-store friction.',
    acquisition_impact: 'The single biggest source of organic app installs: every walk-in becomes a long-term digital patient for life.',
  },
  {
    label: 'Turbo Visibility Accelerator + Auto-SEO landing pages',
    free: false, premium: false, turbo: true,
    service_impact: 'You don\'t need to become an SEO expert — the platform auto-generates specialty+city pages around you.',
    acquisition_impact: 'Capture intent-driven searches like "Cardiologist in Mumbai". Visibility compounds month-over-month without ad spend.',
  },
];

export const PLAN_TIERS = [
  { key: 'free',    name: 'Gold',           price: 'Free',    note: 'First 200 doctors · Rs. 99/mo after', tint: 'amber'   },
  { key: 'premium', name: 'Coffee Premium', price: 'Rs. 299', note: '/month',                               tint: 'sky'     },
  { key: 'turbo',   name: 'TurboCharge',    price: 'Rs. 999', note: '/month',                               tint: 'emerald' },
];
